/**
 * ModuleInfo class implementation, and list of available modules.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © 2025 Ircam Amplify
 */

import { ModuleCategory } from './ModuleDef'

/**
 * Return the URL path segment for this module, for example to acces to
 * its page of job creation.
 */
function getUrlPathSegment(title: string): string {
  return title.toLowerCase().replace(/ /g, '-')
}

/**
 * Information about a module.
 *
 * The class must only store data, and must not make processing or
 * API requests which involve 3rd-party Node features like Redux storage:
 * the class must be usable by Playwright test cases which do not use Vite,
 * Redux, ...
 */
export class ModuleInfo {
  serviceName: string
  endpoint: string
  title: string
  shortTitle: string
  displayModuleName: string
  cat: ModuleCategory
  description: string
  urlPathSegment: string

  constructor({
    serviceName,
    endpoint,
    title,
    shortTitle,
    cat,
    description,
    displayModuleName,
  }: {
    serviceName: string
    endpoint: string
    title: string
    shortTitle: string
    cat: ModuleCategory
    description: string
    displayModuleName?: string
  }) {
    this.serviceName = serviceName
    this.endpoint = endpoint
    this.title = title
    this.shortTitle = shortTitle
    this.displayModuleName = displayModuleName ?? title
    this.cat = cat
    this.description = description
    this.urlPathSegment = getUrlPathSegment(title)
  }
}

/**
 * The unique keys of the modules.
 */
export enum ModuleKey {
  AiMusicDetector,
  AiReEditor,
  AiSpeechDetector,
  AudioTextAligner,
  Converter,
  CoverDetector,
  EditCut,
  EditFadeIn,
  EditFadeOut,
  EditMix,
  EditMute,
  EditPanning,
  EditTransition,
  InfringementDetector,
  LoopExtractor,
  LoudnessAnalyzer,
  MusicTaggerAll,
  MusicTaggerBeats,
  MusicTaggerChords,
  MusicTaggerGenre,
  MusicTaggerInstrument,
  MusicTaggerKey,
  MusicTaggerMood,
  MusicTaggerRhythm,
  MusicTaggerStructure,
  MusicTaggerSummary,
  QualityCheck,
  SpeechToText,
  StemSeparator,
  StereoToSpatial,
  TalkAnalyzer,
  TimePitchControl,
}
